import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { bindActionCreators } from 'redux'
import type { Playlist } from 'types/playlist'
import type { Shortlist } from 'types/shortlist'
import type { Track, TrackId } from 'types/track'
import type { EventSource } from 'types/ui'
import {
  changeVolume,
  nextTrack,
  pauseTrack,
  playTrack,
  preloadNextTrack,
  previousTrack,
  stopTrack,
  updatePlaybackProgress,
  updatePlaybackStatus,
  updateTrackDuration,
} from '@disco/state-management/actions/MediaActions'
import { PLAYING } from '@disco/constants/MediaStatus'
import type { TrackStatus } from '@disco/state-management/reducers/media'
import {
  getNextVideoInPlaylist,
  getPlayingPlaylist,
  getPlayingShortlist,
  getPreloadingTrack,
  getPreviousVideoInPlaylist,
  getTrackPlaybackStatus,
  getTrackPlaying,
} from '@disco/state-management/selectors/media'
import { isTargetEditable, keyEqualTo } from '@disco/utils/keyboard'
import PlaybackManager from 'components/common/player/playback-manager/loader'
import useEventListener from 'hooks/use-event-listener'
const DEFAULT_TRACK_STATUS = {}
type Props = {
  campaignId?: number | null | undefined
  mediaSource: EventSource
  modalStyle?: 'default' | 'overlay'
  recipientId?: number | null | undefined
  groupId?: number | null | undefined
  shareId?: number | null | undefined
  location: string | null | undefined
  disableAutoplay?: boolean
}

/**
 * Player playback manager component that handles media playback state and keyboard controls
 * Manages Redux actions for track playback, provides keyboard shortcuts, and coordinates
 * with the underlying PlaybackManager for audio/video playback functionality
 * @param campaignId - Optional campaign ID for tracking
 * @param mediaSource - Source context for the media playback
 * @param modalStyle - Display style for video modals
 * @param recipientId - Optional recipient ID for sharing context
 * @param groupId - Optional group ID for context
 * @param shareId - Optional share ID for tracking
 * @param location - Location context for playback
 * @param disableAutoplay - Whether to disable automatic playback
 */
const PlayerPlaybackManager = ({
  campaignId,
  mediaSource,
  modalStyle,
  recipientId,
  groupId,
  shareId,
  location,
  disableAutoplay,
}: Props) => {
  const dispatch = useDispatch()
  const boundMediaActions = useMemo(
    () =>
      bindActionCreators(
        {
          changeVolume,
          nextTrack,
          preloadNextTrack,
          previousTrack,
          stopTrack,
          updatePlaybackProgress,
          updatePlaybackStatus,
          updateTrackDuration,
        },
        dispatch,
      ),
    [dispatch],
  )
  const currentTrack: Track | null | undefined = useSelector(getTrackPlaying)
  const preloadingTrack: Track | null | undefined =
    useSelector(getPreloadingTrack)
  const trackPlaylist: Playlist | null | undefined =
    useSelector(getPlayingPlaylist)
  const trackShortlist: Shortlist | null | undefined =
    useSelector(getPlayingShortlist)
  const tracklist: TrackId[] = useSelector(state => state.media.tracklist)
  const clientLibraryId = useSelector(state => state.media.clientLibraryId)
  const volume: number = useSelector(state => state.media.volume)
  // TODO: Add types
  const nextVideo = useSelector(getNextVideoInPlaylist)
  const prevVideo = useSelector(getPreviousVideoInPlaylist)
  const {
    loadStatus,
    playbackPosition,
    playbackStatus,
    playbackLocation,
  }: TrackStatus =
    useSelector(state =>
      getTrackPlaybackStatus(state, {
        trackId: currentTrack?.id,
      }),
    ) ?? (DEFAULT_TRACK_STATUS as TrackStatus)
  useEffect(() => {
    PlaybackManager.preload()
  }, [])

  const handleKeyDown = (event: KeyboardEvent): void => {
    const validTarget = !isTargetEditable(event)
    const trackToPlay = currentTrack || preloadingTrack
    if (!validTarget || !trackToPlay) return
    const isSpacebar =
      keyEqualTo(event, ' ') &&
      !event.altKey &&
      !event.shiftKey &&
      !event.metaKey &&
      !event.ctrlKey

    if (isSpacebar) {
      // prevent default pane scrolling, etc
      event.stopPropagation()
      event.preventDefault()

      if (playbackStatus !== PLAYING) {
        dispatch(
          playTrack(trackToPlay.id, mediaSource, {
            // TODO: replace with playbackContext
            // Keep the pane that was playing; panes[index] may hold a newer search by now
            queue: true,
            playlistId: trackPlaylist?.playlist_id,
            shortlistId: trackShortlist?.id,
            groupId,
            campaignId,
            recipientId,
            shareId,
            tracklist,
            clientLibraryId,
            // Preserve current playback location when resuming via spacebar
            playbackLocation,
          }),
        )
      } else {
        dispatch(pauseTrack(trackToPlay.id))
      }
    }

    const isForward = event.shiftKey && keyEqualTo(event, 'ArrowRight')
    const isBackward = event.shiftKey && keyEqualTo(event, 'ArrowLeft')

    if (isForward || isBackward) {
      event.stopPropagation()
      event.preventDefault()
      if (isForward) dispatch(nextTrack(false))
      if (isBackward) dispatch(previousTrack())
    }
  }

  useEventListener('keydown', handleKeyDown)
  return (
    <PlaybackManager
      changeVolume={boundMediaActions.changeVolume}
      loadStatus={loadStatus}
      location={location}
      modalStyle={modalStyle}
      nextTrack={boundMediaActions.nextTrack}
      nextVideo={nextVideo}
      playbackPosition={playbackPosition}
      playbackStatus={playbackStatus}
      preloadingTrack={preloadingTrack}
      playbackLocation={playbackLocation}
      preloadNextTrack={boundMediaActions.preloadNextTrack}
      previousTrack={boundMediaActions.previousTrack}
      prevVideo={prevVideo}
      stopTrack={boundMediaActions.stopTrack}
      track={currentTrack}
      updatePlaybackProgress={boundMediaActions.updatePlaybackProgress}
      updatePlaybackStatus={boundMediaActions.updatePlaybackStatus}
      updateTrackDuration={boundMediaActions.updateTrackDuration}
      volume={volume}
      disableAutoplay={disableAutoplay}
    />
  )
}

export default React.memo<Props>(PlayerPlaybackManager)
